import { getFieldsByDriver, secondDriverOption } from '../configs/constants';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormBuilder,
  Separator,
  StepperFooter,
  buildFormValuesFromFields,
  getResolver,
  useAssertedParams,
  useStepperContext,
} from '@folio/index';
import { useQuery } from '@tanstack/react-query';
import { keyValueFieldsToObject, policyControllerGetPolicyQueryOptions } from 'korint-sdk';
import type { Field } from 'korint-sdk/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const driverDeclarationFields: Array<Field> = [
  {
    name: 'noTerminationForAnyReason',
    required: true,

    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'noTerminationForNonPayment',
    required: true,

    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'noDriverLicenseSuspension',
    required: true,

    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'noConvictionForDrunkDriving',
    required: true,

    type: 'checkbox',
    isCustomField: true,
  },
] as const;

const firstDriverDeclarationFields = getFieldsByDriver(driverDeclarationFields, '1') as Field[];
const usageField: Field = {
  name: 'noPublicGoodsTransport',
  type: 'checkbox',
  required: true,
  isCustomField: true,
};
const secondDriverDeclarationFields = getFieldsByDriver(driverDeclarationFields, '2') as Field[];

export function PolicyholderDeclarationsStep() {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();
  const stepper = useStepperContext();

  const { data: policy, isLoading } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    enabled: !!policyId,
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const hasSecondDriver = policy?.customFields?.[secondDriverOption.name];

  const driverFields = hasSecondDriver
    ? [...firstDriverDeclarationFields, usageField, ...secondDriverDeclarationFields]
    : [...firstDriverDeclarationFields, usageField];

  const form = useForm({
    values: buildFormValuesFromFields(driverFields, {}),
    resolver: getResolver(driverFields),
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('policyholderDeclarations.mainDriverTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-semibold font-sm pb-2">
            {t('policyholderDeclarations.mainDriverIntro')}
          </p>
          <FormBuilder fields={firstDriverDeclarationFields} form={form} isLoading={isLoading} />

          <Separator className="my-4" />

          <p className="font-semibold font-sm pb-2">
            {t('policyholderDeclarations.vehicleUsageIntro')}
          </p>
          <FormBuilder fields={[usageField]} form={form} isLoading={isLoading} />
        </CardContent>
      </Card>

      {hasSecondDriver && (
        <Card>
          <CardHeader>
            <CardTitle>{t('policyholderDeclarations.secondDriverTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-semibold pb-2">{t('policyholderDeclarations.secondDriverIntro')}</p>
            <FormBuilder fields={secondDriverDeclarationFields} form={form} isLoading={isLoading} />
          </CardContent>
        </Card>
      )}

      <StepperFooter
        nextButtonEnabled
        onNext={form.handleSubmit(() => {
          if (!policyId) return;

          stepper.next();
        })}
      />
    </div>
  );
}
